import { useLocation, useNavigate } from 'react-router-dom'
import { Coins, HelpCircle, LogOut, User } from 'lucide-react'
import { useAuthStore } from '@/stores/auth'
import { useFeatureGuideStore } from '@/stores/featureGuide'
import { getFeatureByRoute } from '@/components/guides/guide-content'
import { cn, formatCredits } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

const PAGE_TITLES: Record<
  string,
  { title: string; subtitle?: string; section?: string; accent?: string }
> = {
  '/home': { title: 'Overview', accent: 'bg-spectrum-blue' },
  '/generate/image/competitor': {
    title: 'Competitor Research',
    subtitle: 'Generate creatives based on competitor ads',
    section: 'Image',
    accent: 'bg-spectrum-teal',
  },
  '/generate/image/variation': {
    title: 'Variation',
    subtitle: 'Create variations of your existing creatives',
    section: 'Image',
    accent: 'bg-spectrum-cyan',
  },
  '/generate/image/freestyle': {
    title: 'Image Freestyle',
    subtitle: 'Generate images from your own prompts',
    section: 'Image',
    accent: 'bg-spectrum-blue',
  },
  '/generate/image': {
    title: 'Generate Images',
    subtitle: 'Create images using AI',
    section: 'Image',
    accent: 'bg-spectrum-blue',
  },
  '/generate/video/digital-human': {
    title: 'Digital Human',
    subtitle: 'Create AI spokesperson videos',
    section: 'Video',
    accent: 'bg-spectrum-magenta',
  },
  '/generate/video/freestyle': {
    title: 'Video Freestyle',
    subtitle: 'Generate videos with AI',
    section: 'Video',
    accent: 'bg-spectrum-purple',
  },
  '/generate/video': {
    title: 'Generate Video',
    subtitle: 'Create videos using AI',
    section: 'Video',
    accent: 'bg-spectrum-purple',
  },
  '/creative-lab/ideas': {
    title: 'Ideas',
    subtitle: 'All ideas + Ads Research + Web Research + Deep Research',
    section: 'Creative Lab',
    accent: 'bg-spectrum-lime',
  },
  '/creative-lab/vertical-ideas': {
    title: 'Vertical Ideas',
    subtitle: 'Product-type workflows for unique ideas',
    section: 'Creative Lab',
    accent: 'bg-spectrum-emerald',
  },
  '/creative-lab': {
    title: 'Creative Lab',
    subtitle: 'Research and ideation tools',
    accent: 'bg-spectrum-spring',
  },
  '/products/new': {
    title: 'Create Product',
    subtitle: 'Add a new product to your catalog',
    accent: 'bg-spectrum-coral',
  },
  '/products': {
    title: 'Products',
    subtitle: 'Manage your product catalog',
    accent: 'bg-spectrum-coral',
  },
  '/library': {
    title: 'Library',
    subtitle: 'Browse and manage your assets',
    accent: 'bg-spectrum-orange',
  },
  '/drama': {
    title: 'Drama Mix',
    subtitle: 'Turn full short-dramas into mixed-cut promo videos',
    accent: 'bg-spectrum-magenta',
  },
  '/tasks': { title: 'Tasks', subtitle: 'Track generation progress', accent: 'bg-spectrum-orange' },
  '/settings/subscription': {
    title: 'Subscription',
    subtitle: 'Manage your plan and credits',
    accent: 'bg-spectrum-coral',
  },
  '/settings/billing': {
    title: 'Billing History',
    subtitle: 'View transaction history',
    accent: 'bg-spectrum-coral',
  },
  '/settings': { title: 'Profile', subtitle: 'Account settings', accent: 'bg-spectrum-coral' },
}

function getPageInfo(pathname: string): {
  title: string
  subtitle?: string
  section?: string
  accent?: string
} {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname]
  for (const [path, info] of Object.entries(PAGE_TITLES)) {
    if (pathname.startsWith(path)) return info
  }
  return { title: 'Overview' }
}

function getInitials(email?: string): string {
  if (!email) return 'U'
  const name = email.split('@')[0]
  const parts = name.split(/[._-]/)
  return parts
    .map((p) => p[0]?.toUpperCase())
    .filter(Boolean)
    .slice(0, 2)
    .join('')
}

function getPlanBadgeStyle(planType: string): string {
  switch (planType) {
    case 'trial':
      return 'text-gray-600 bg-gray-100'
    case 'basic':
      return 'text-blue-600 bg-blue-100'
    case 'pro':
      return 'text-purple-600 bg-purple-100'
    case 'enterprise':
      return 'text-amber-600 bg-amber-100'
    default:
      return 'text-gray-600 bg-gray-100'
  }
}

function getHeaderAccentTheme(accent?: string): {
  markerGradient: string
  sectionPill: string
  titleAccent: string
} {
  switch (accent) {
    case 'bg-spectrum-teal':
      return {
        markerGradient: 'from-spectrum-teal to-spectrum-cyan',
        sectionPill: 'bg-spectrum-teal/10 text-spectrum-teal',
        titleAccent: 'group-hover:text-spectrum-teal',
      }
    case 'bg-spectrum-cyan':
      return {
        markerGradient: 'from-spectrum-cyan to-spectrum-blue',
        sectionPill: 'bg-spectrum-cyan/10 text-spectrum-cyan',
        titleAccent: 'group-hover:text-spectrum-cyan',
      }
    case 'bg-spectrum-blue':
      return {
        markerGradient: 'from-spectrum-blue to-spectrum-cyan',
        sectionPill: 'bg-spectrum-blue/10 text-spectrum-blue',
        titleAccent: 'group-hover:text-spectrum-blue',
      }
    case 'bg-spectrum-purple':
      return {
        markerGradient: 'from-spectrum-purple to-spectrum-blue',
        sectionPill: 'bg-spectrum-purple/10 text-spectrum-purple',
        titleAccent: 'group-hover:text-spectrum-purple',
      }
    case 'bg-spectrum-magenta':
      return {
        markerGradient: 'from-spectrum-magenta to-spectrum-purple',
        sectionPill: 'bg-spectrum-magenta/10 text-spectrum-magenta',
        titleAccent: 'group-hover:text-spectrum-magenta',
      }
    case 'bg-spectrum-coral':
      return {
        markerGradient: 'from-spectrum-coral to-spectrum-orange',
        sectionPill: 'bg-spectrum-coral/10 text-spectrum-coral',
        titleAccent: 'group-hover:text-spectrum-coral',
      }
    case 'bg-spectrum-orange':
      return {
        markerGradient: 'from-spectrum-orange to-spectrum-amber',
        sectionPill: 'bg-spectrum-orange/10 text-spectrum-orange',
        titleAccent: 'group-hover:text-spectrum-orange',
      }
    case 'bg-spectrum-lime':
      return {
        markerGradient: 'from-spectrum-lime to-spectrum-spring',
        sectionPill: 'bg-spectrum-lime/10 text-spectrum-lime',
        titleAccent: 'group-hover:text-spectrum-lime',
      }
    case 'bg-spectrum-spring':
      return {
        markerGradient: 'from-spectrum-spring to-spectrum-emerald',
        sectionPill: 'bg-spectrum-spring/10 text-spectrum-spring',
        titleAccent: 'group-hover:text-spectrum-spring',
      }
    case 'bg-spectrum-emerald':
      return {
        markerGradient: 'from-spectrum-emerald to-spectrum-spring',
        sectionPill: 'bg-spectrum-emerald/10 text-spectrum-emerald',
        titleAccent: 'group-hover:text-spectrum-emerald',
      }
    case 'bg-spectrum-amber':
      return {
        markerGradient: 'from-spectrum-amber to-spectrum-orange',
        sectionPill: 'bg-spectrum-amber/10 text-spectrum-amber',
        titleAccent: 'group-hover:text-spectrum-amber',
      }
    default:
      return {
        markerGradient: 'from-gray-500 to-gray-300',
        sectionPill: 'bg-gray-100 text-gray-500',
        titleAccent: 'group-hover:text-gray-700',
      }
  }
}

export function Header() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, subscription, logout } = useAuthStore()
  const openGuide = useFeatureGuideStore((s) => s.openGuide)
  const pageInfo = getPageInfo(location.pathname)
  const feature = getFeatureByRoute(location.pathname)
  const displayPlanType = subscription?.is_expired ? 'trial' : subscription?.plan_type
  const displayPlanLabel = subscription?.is_expired ? 'trial' : subscription?.plan_type
  const accentTheme = getHeaderAccentTheme(pageInfo.accent)

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <header
      className={cn(
        'h-20 border-b border-gray-200/60 px-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]',
        'flex items-center justify-between',
        'bg-gradient-to-r from-white via-slate-50/70 to-white'
      )}
    >
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'h-2.5 w-8 rounded-full bg-gradient-to-r shadow-sm',
            accentTheme.markerGradient
          )}
        />

        <div className="flex items-center gap-2">
          {pageInfo.section && (
            <>
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-xs font-semibold',
                  accentTheme.sectionPill
                )}
              >
                {pageInfo.section}
              </span>
              <span className="text-gray-300">/</span>
            </>
          )}
          {feature ? (
            <button
              onClick={() => openGuide(feature.featureKey)}
              className="group flex cursor-pointer items-center gap-1.5"
            >
              <h2
                className={cn(
                  'text-base font-semibold text-gray-900 transition-colors',
                  accentTheme.titleAccent
                )}
              >
                {pageInfo.title}
              </h2>
              <HelpCircle className="h-3.5 w-3.5 text-gray-300 transition-colors group-hover:text-gray-400" />
            </button>
          ) : (
            <h2 className="text-base font-semibold text-gray-900">{pageInfo.title}</h2>
          )}
        </div>

        {pageInfo.subtitle && (
          <>
            <span className="hidden h-4 w-px bg-gray-200 sm:block" />
            <p className="hidden text-sm text-gray-400 sm:block">{pageInfo.subtitle}</p>
          </>
        )}
      </div>

      <div className="flex items-center gap-3">
        {subscription && (
          <div className="flex items-center gap-3 rounded-full border border-gray-200/80 bg-white px-3.5 py-1.5 shadow-sm transition-all hover:border-gray-300 hover:shadow-md">
            <span
              className={cn(
                'rounded-full px-2.5 py-1 text-xs font-semibold capitalize',
                getPlanBadgeStyle(displayPlanType || 'trial')
              )}
            >
              {displayPlanLabel}
            </span>

            <span className="hidden h-4 w-px bg-gray-200 md:block" />

            <div className="flex items-center gap-2">
              <Coins className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-semibold text-gray-900">
                {formatCredits(subscription.credits_available)}
              </span>
              <span className="hidden text-xs text-muted-foreground sm:inline">credits</span>
            </div>
          </div>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex items-center gap-2 rounded-full border border-gray-200/80 bg-white px-2 py-1 shadow-sm transition-all hover:border-gray-300 hover:shadow-md"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-spectrum-blue to-spectrum-purple">
                <span className="text-xs font-semibold text-white">{getInitials(user?.email)}</span>
              </div>
              <span className="hidden max-w-[180px] truncate text-sm font-medium text-gray-700 lg:inline">
                {user?.email || 'User'}
              </span>
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <User className="mr-2 h-4 w-4" />
              <span>Profile Settings</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/settings/subscription')}>
              <Coins className="mr-2 h-4 w-4" />
              <span>Subscription</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-red-600">
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
